import { useAuthContext } from "../../auth/state/AuthContext";
import { Certificate } from "../../certificates/models/certificates.interfaces";
import { updateDocumentInformation } from "../../documents/services/documents.service";
import { themeAtom, userAtom } from "../../shared/atoms";
import { ErrorDisplay } from "../../shared/components/ErrorDisplay";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { toastError, toastSuccess } from "../../shared/components/ToastContainer";
import { log, logE, logV } from "../../shared/services/debug-logger";
import { errorLogger } from "../../shared/services/error-logger";
import { api, apiUrl, postData } from "../../shared/services/service";
import { generateUUID } from "../../shared/services/utils";
import { bqFormsExportAtom, resetBalticFormStorageAtom } from "../state/baltic-form.atoms";
import { datadogRum } from "@datadog/browser-rum";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { twMerge } from "tailwind-merge";

interface FormData {
  [key: string]: any;
}

export type StepNineFinalProps = {
  isCompletedForm: boolean;
  goToLandingPage: () => void;
  goToPrev: () => void;
};

export const Bq11Confirmation = ({ isCompletedForm, goToLandingPage, goToPrev }: StepNineFinalProps) => {
  const theme = useAtomValue(themeAtom);

  const user = useAtomValue(userAtom);
  const bq = useAtomValue<FormData>(bqFormsExportAtom);
  const resetBalticFormStorage = useSetAtom(resetBalticFormStorageAtom);
  const { sub: email } = useAuthContext().state.user?.accessToken || {};
  const { vesselName, imoNumber } = useParams();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  logV("Bq11Confirmation - BUILD", { loading: isLoading });
  const today = new Date();
  const formattedDate = today.toDateString();
  const nameArray = vesselName?.split(" ");
  const firstName = nameArray?.[0]?.charAt(0) || "";
  const lastNameFirstLetter = (nameArray?.length ?? 0) > 1 ? nameArray?.[1]?.charAt(0) : "";

  // We have the full bq sent from FE mostly for legacy reasons.
  // By the time we split the BQ in multiple save stages the export was already done.
  // So we chose the less demanding path by keeping the same setup instead of reading from BE side the BQ forms.
  // Could be migrated to BE eventually.
  const exportAndSubmitBQ = async () => {
    log("Bq11Confirmation - exportAndSubmitBQ");
    try {
      setIsLoading(true);

      const _bq = { bq: { imoNumber, vesselName, ...bq } };

      const expiresOn = new Date(today);
      expiresOn.setMonth(expiresOn.getMonth() + 6);
      const ENV = process.env.REACT_APP_RUM_ENV;

      const bqDataForData: Certificate = {
        fileName: "Baltic questionnaire.docx",
        vesselCode: "",
        vesselName: vesselName || "",
        imoNumber: imoNumber || "",
        docName: "Baltic questionnaire",
        docType: "Questionnaires",
        source: "Vessel submission",
        expiresOn: expiresOn,
        nextSurveyDate: null,
        expiryNotApplicable: false,
        portOfIssueIsWhoApproved: false,
        s3Path: `${ENV}/${generateUUID()}.docx`,
      };

      const response = await postData(`exportBQ`, _bq); // Export and generate email
      log("Bq11Confirmation - exportAndSubmitBQ - export result", { success: !!response.data?.success });
      if (response.data?.success) {
        await updateDocumentInformation(bqDataForData, user); // Add Baltic Questionnaire inside DOCUMENTS Table
        toastSuccess("Form exported successfully");
        clearStorageForms();
        submitBalticForm(); // Mark BQ as submited in SQL, by now each page was saved individually via stored procedure.
      }
    } catch (error) {
      logE(new Error("Bq11Confirmation - exportAndSubmitBQ - failed"));
      handleFail("Failed to export and submit BQ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFail = (msg: string, error: any) => {
    toastError(
      `Source: ${msg} - Msg: ${error.message} - Url: ${error.config.url} - Status: ${error.request.statusText} - Err: ${error.request.response}`,
    );
  };

  const submitBalticForm = async () => {
    try {
      setIsLoading(true);
      const requestData = {
        ext_user_email: email,
        vessel_name: vesselName,
        imo_number: imoNumber,
      };
      const result = await api.post(`${apiUrl}/extUserBalticFormSubmission`, requestData, { params: requestData });
      log("REPO - submitBalticForm - response", { status: result.status });
      setData(result.data.data);
      goToLandingPageOnCompletion();
    } catch (error: unknown) {
      logE(new Error("Bq11Confirmation - submitBalticForm - failed"));
      handleFail("Failed to submit BQ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const goToLandingPageOnCompletion = () => {
    if (data) {
      setTimeout(() => {
        if (!isCompletedForm) {
          // Originally, this was only tracked in the normal step but not in the completed one. The logic has been maintained.
          datadogRum.addAction("balticFormSubmitted", {
            ext_user_email: email,
            vessel_name: vesselName,
            imo_number: imoNumber,
          });
        }
        goToLandingPage();
      }, 3000);
    }
  };

  const clearStorageForms = () => {
    resetBalticFormStorage();
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
        )}
      >
        <h1 className="custom-heading pb-4">Confirm submission</h1>
        <ToastContainer />
        <div className="m-0 w-3/5 p-1 text-sm">
          <ErrorDisplay category="BQ" />
        </div>

        <div className="pb-4">
          <label>You’re about to send the Baltic Questionnaire to Cargill. Please, confirm your submission.</label>
        </div>

        <div className="w-3/5">
          <div className={twMerge("grid grid-cols-5 gap-4 rounded-lg p-2", theme.balticQuestionnaire.lastScreenBg)}>
            <div className="mb-4">
              <div className="block pb-2 text-sm">Submitted by</div>
              <div className="flex justify-start">
                <p
                  className={twMerge(
                    "rounded-full px-1 text-sm",
                    theme.sideBarComponent.tailWind.white,
                    theme.headerComponent.logInCircle.text,
                  )}
                >
                  {firstName}
                  {lastNameFirstLetter}
                </p>
                <p className="pl-2 text-sm">{vesselName}</p>
              </div>
            </div>
            <div className="mb-4">
              <div className="block pb-2 text-sm">Date</div>
              <div className="text-sm">{formattedDate}</div>
            </div>
            <div className="mb-4">
              <div className="block pb-2 text-sm">Vessel name</div>
              <div className="text-sm">{vesselName}</div>
            </div>
            <div className="mb-4">
              <div className="block pb-2 text-sm">IMO Number</div>
              <div className="flex items-center text-sm">{imoNumber}</div>
            </div>
          </div>

          {/* Prev or Submit */}
          <div className="flex justify-start pt-4">
            <button
              className={twMerge(
                "m-2 rounded px-4 py-2 focus:outline-none focus:ring",
                theme.myVesselsTableRow.row.primary,
                theme.headerComponent.tailwind.buttonText,
              )}
              onClick={goToPrev}
            >
              Previous
            </button>
            <button
              type="submit"
              onClick={exportAndSubmitBQ}
              disabled={errorLogger.hasErrorsByCategory("BQ")}
              className={twMerge(
                "m-2 rounded px-4 py-2 focus:outline-none focus:ring",
                errorLogger.hasErrorsByCategory("BQ")
                  ? twMerge(
                      "cursor-not-allowed text-gray-500",
                      theme.balticQuestionnaire.dividerBg,
                      theme.myVesselsTableRow.tableText.secondary,
                    )
                  : twMerge(theme.uwhcAssistant.sendBg, theme.uwhcAssistant.sendText),
              )}
            >
              Submit
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2" />
      </div>
    </LoadingWrapper>
  );
};
